export const Unit = {
  INCHES_DECIMAL: "inches_decimal",
  INCHES_FRACTIONAL: "inches_fractional",
  FEET_INCHES: "feet_inches",
  MILLIMETERS: "millimeters",
} as const;

export type Unit = (typeof Unit)[keyof typeof Unit];

export const MM_PER_INCH = 25.4;
export const CM_PER_INCH = 2.54;
export const M_PER_INCH = 0.0254;
export const INCHES_PER_FOOT = 12;

export const VALID_PRECISIONS: Readonly<Record<Unit, ReadonlySet<string>>> = {
  [Unit.INCHES_FRACTIONAL]: new Set(["1", "1/2", "1/4", "1/8", "1/16", "1/32"]),
  [Unit.FEET_INCHES]: new Set(["1", "1/2", "1/4", "1/8", "1/16", "1/32"]),
  [Unit.INCHES_DECIMAL]: new Set(["1", "0.1", "0.01", "0.001"]),
  [Unit.MILLIMETERS]: new Set(["1", "0.1", "0.01"]),
};

/**
 * Result of parsing a user-entered dimension string.
 * `inches` is always decimal inches (the canonical storage unit) when `ok` is true.
 * `detectedUnit` reflects what the input string looked like, not the caller's mode.
 * `error` is populated only when `ok` is false.
 */
export interface ParseResult {
  readonly ok: boolean;
  readonly inches: number;
  readonly detectedUnit: Unit | null;
  readonly error: string | null;
}

export function isUnit(x: unknown): x is Unit {
  return typeof x === "string" && (Object.values(Unit) as string[]).includes(x);
}

export function validatePrecision(unit: Unit, precision: string): void {
  if (!isUnit(unit)) {
    throw new TypeError(
      `unit must be a Unit value, got ${typeof unit}: ${String(unit)}`,
    );
  }
  if (typeof precision !== "string") {
    throw new TypeError(
      `precision must be a string, got ${typeof precision}: ${String(precision)}`,
    );
  }
  const allowed = VALID_PRECISIONS[unit];
  if (!allowed.has(precision)) {
    const allowedList = [...allowed].sort();
    throw new Error(
      `precision ${JSON.stringify(precision)} is not valid for ${unit}; allowed: ${JSON.stringify(allowedList)}`,
    );
  }
}
